import { INJECTION_KEYS } from '#imports';
import OrderAPI from '~/api/order';
import type { ResponseError } from '~/api/utils/types';

export default (options: {
  onChecking: () => Promise<{ orderId: number; enabled: boolean }>;
  onSuccess?: () => void | Promise<void>;
  onClear?: () => void;
  onCancel?: () => void;
}) => {
  const { t } = useI18n({ useScope: 'global' });
  const modalStore = useModalStore();
  const payment = inject(INJECTION_KEYS.PaymentMethods);

  if (!payment) throw new Error('Payment methods are not provided');

  const isLoading = ref(false);
  const isLeaving = ref(true);

  const execute = async () => {
    await payment.ready(isLoading);

    try {
      if (isLoading.value) return;
      isLoading.value = true;

      const { orderId, enabled } = await options.onChecking();

      if (!enabled) {
        options.onClear?.();
        options.onSuccess?.();
        return;
      }

      const paymentTokenData = await OrderAPI.getPaymentToken({
        params: { orderId },
      });

      await payment.execute({
        token: paymentTokenData.token,
        onError: () => {
          modalStore.showError({
            title: t('composables.payment.message.widget_error'),
            action: t('composables.payment.message.action'),
            onClose: options.onClear,
          });
        },
        onSuccess: () => {
          options.onSuccess?.();
          options.onClear?.();
        },
        onCancel: () => {
          options.onCancel?.();
          options.onClear?.();
        },
      });
    } catch (error) {
      logError(error, 'Ошибка исполнения заказа и платежа');
      modalStore.showError({
        title: getFetchErrorMessage(
          error as ResponseError,
          t('composables.payment.message.order_error'),
        ),
        action: t('composables.payment.message.action'),
        onClose: options.onClear,
      });
    } finally {
      isLoading.value = false;
    }
  };

  onBeforeUnmount(() => {
    isLeaving.value = false;
  });

  return { isLoading, execute };
};
